/**
 * Triangle Background Animations
 *
 * Animation functions that compute per-triangle transforms based on time,
 * position, and mouse input.
 */

import { animatedNoise, fbm, seed } from './noise';

export interface AnimationState {
  scale: number;
  opacity: number;
  rotationOffset: number;
}

export interface AnimationContext {
  time: number;           // Elapsed time in seconds
  mouseX: number;         // Mouse X (0-1 normalized)
  mouseY: number;         // Mouse Y (0-1 normalized)
  mouseActive: boolean;   // Is mouse over canvas
}

export interface TrianglePosition {
  x: number;              // Normalized X (0-1)
  y: number;              // Normalized Y (0-1)
  index: number;          // Triangle index for seeding
}

export type AnimationFunction = (
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig
) => AnimationState;

export interface AnimationConfig {
  speed: number;          // Animation speed multiplier
  intensity: number;      // Effect intensity (0-1)
  noiseScale: number;     // Noise sampling scale
  mouseRadius: number;    // Mouse effect radius (0-1)
  mouseStrength: number;  // Mouse effect strength
}

const DEFAULT_CONFIG: AnimationConfig = {
  speed: 1,
  intensity: 0.5,
  noiseScale: 2,
  mouseRadius: 0.15,
  mouseStrength: 0.8,
};

/**
 * Swarm Animation
 * Organic, hive-mind movement using Perlin noise
 */
export function swarmAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time, mouseX, mouseY, mouseActive } = ctx;
  const { speed, intensity, noiseScale } = config;

  // Sample noise at triangle position + time
  const nx = pos.x * noiseScale;
  const ny = pos.y * noiseScale;
  const nt = time * speed * 0.3;

  // Primary noise for scale
  const noiseVal = animatedNoise(nx, ny, nt);

  // Secondary noise for opacity (different frequency)
  const opacityNoise = animatedNoise(nx * 1.5 + 100, ny * 1.5, nt * 0.7);

  // Base scale with noise
  let scale = 0.6 + (noiseVal * 0.5 + 0.5) * intensity * 0.8;

  // Opacity variation
  let opacity = 0.4 + (opacityNoise * 0.5 + 0.5) * 0.6;

  // Mouse repulsion
  if (mouseActive) {
    const dx = pos.x - mouseX;
    const dy = pos.y - mouseY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < config.mouseRadius) {
      const falloff = 1 - dist / config.mouseRadius;
      const repulsion = falloff * falloff * config.mouseStrength;
      scale *= 1 - repulsion * 0.7;
      opacity *= 1 - repulsion * 0.3;
    }
  }

  // Subtle rotation based on noise
  const rotationOffset = noiseVal * intensity * 5;

  return { scale, opacity, rotationOffset };
}

/**
 * Wave Animation
 * Sinusoidal waves traveling across the grid
 */
export function waveAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time, mouseX, mouseY, mouseActive } = ctx;
  const { speed, intensity } = config;

  // Wave parameters
  const waveFreq = 4;
  const waveSpeed = time * speed;

  // Diagonal wave
  const wave1 = Math.sin((pos.x + pos.y) * waveFreq - waveSpeed);
  // Horizontal wave
  const wave2 = Math.sin(pos.x * waveFreq * 1.5 - waveSpeed * 1.3);
  // Combined waves
  const combined = (wave1 + wave2 * 0.5) / 1.5;

  let scale = 0.5 + (combined * 0.5 + 0.5) * intensity;
  let opacity = 0.3 + (combined * 0.5 + 0.5) * 0.7;

  // Mouse attraction (opposite of repulsion)
  if (mouseActive) {
    const dx = pos.x - mouseX;
    const dy = pos.y - mouseY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < config.mouseRadius * 1.5) {
      const falloff = 1 - dist / (config.mouseRadius * 1.5);
      const attraction = falloff * falloff * config.mouseStrength * 0.5;
      scale *= 1 + attraction * 0.5;
      opacity = Math.min(1, opacity + attraction * 0.3);
    }
  }

  return { scale, opacity, rotationOffset: 0 };
}

/**
 * Pulse Animation
 * Radial pulses emanating from center
 */
export function pulseAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time, mouseX, mouseY, mouseActive } = ctx;
  const { speed, intensity } = config;

  // Distance from center
  const centerX = mouseActive ? mouseX : 0.5;
  const centerY = mouseActive ? mouseY : 0.5;
  const dx = pos.x - centerX;
  const dy = pos.y - centerY;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Expanding rings
  const ringFreq = 6;
  const ringSpeed = time * speed * 2;
  const ring = Math.sin(dist * ringFreq - ringSpeed);

  // Multiple rings at different frequencies
  const ring2 = Math.sin(dist * ringFreq * 0.7 - ringSpeed * 0.8) * 0.5;
  const combined = (ring + ring2) / 1.5;

  const scale = 0.4 + (combined * 0.5 + 0.5) * intensity * 0.8;
  const opacity = 0.3 + (combined * 0.5 + 0.5) * 0.7;

  return { scale, opacity, rotationOffset: 0 };
}

/**
 * Vortex Animation
 * Spiral rotation around center
 */
export function vortexAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time, mouseX, mouseY, mouseActive } = ctx;
  const { speed, intensity } = config;

  // Distance and angle from center
  const centerX = mouseActive ? mouseX : 0.5;
  const centerY = mouseActive ? mouseY : 0.5;
  const dx = pos.x - centerX;
  const dy = pos.y - centerY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);

  // Spiral effect - rotation increases with distance
  const spiral = Math.sin(angle * 3 + dist * 8 - time * speed * 2);

  // Scale based on spiral + distance falloff
  const distFalloff = 1 - Math.min(dist * 1.5, 1);
  const scale = 0.4 + spiral * 0.3 * intensity + distFalloff * 0.3;
  const opacity = 0.3 + (spiral * 0.5 + 0.5) * 0.5 + distFalloff * 0.2;

  // Rotation follows the spiral
  const rotationOffset = (angle + time * speed) * 10 * intensity;

  return { scale, opacity, rotationOffset };
}

/**
 * Breathe Animation
 * Gentle, synchronized pulsing
 */
export function breatheAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time } = ctx;
  const { speed, intensity } = config;

  // Slow, smooth breathing
  const breathe = Math.sin(time * speed * 0.5) * 0.5 + 0.5;

  // Slight variation based on position
  const offset = (pos.x + pos.y) * 0.2;
  const localBreathe = Math.sin(time * speed * 0.5 + offset) * 0.5 + 0.5;

  const combined = breathe * 0.7 + localBreathe * 0.3;

  const scale = 0.5 + combined * intensity * 0.5;
  const opacity = 0.4 + combined * 0.5;

  return { scale, opacity, rotationOffset: 0 };
}

/**
 * Flow Field Animation
 * Simulates particles following a vector field
 */
export function flowAnimation(
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  const { time } = ctx;
  const { speed, intensity, noiseScale } = config;

  // Sample noise for flow direction
  const nx = pos.x * noiseScale;
  const ny = pos.y * noiseScale;
  const nt = time * speed * 0.2;

  // Flow angle from noise
  const flowAngle = animatedNoise(nx, ny, nt) * Math.PI * 2;

  // Flow intensity from different noise sample
  const flowIntensity = (animatedNoise(nx + 50, ny + 50, nt) + 1) * 0.5;

  // Directional effect based on flow
  const dirEffect = Math.sin(pos.x * 10 + flowAngle + time * speed);

  const scale = 0.5 + flowIntensity * intensity * 0.5;
  const opacity = 0.3 + flowIntensity * 0.5 + dirEffect * 0.2;
  const rotationOffset = flowAngle * 30 * intensity;

  return { scale, opacity, rotationOffset };
}

// Animation registry
export const animations: Record<string, AnimationFunction> = {
  swarm: swarmAnimation,
  wave: waveAnimation,
  pulse: pulseAnimation,
  vortex: vortexAnimation,
  breathe: breatheAnimation,
  flow: flowAnimation,
};

export type AnimationType = keyof typeof animations;

/**
 * Create a blended animation from multiple sources
 */
export function blendAnimations(
  animationConfigs: Array<{ type: AnimationType; weight: number }>,
  pos: TrianglePosition,
  ctx: AnimationContext,
  config: AnimationConfig = DEFAULT_CONFIG
): AnimationState {
  let totalWeight = 0;
  let scale = 0;
  let opacity = 0;
  let rotationOffset = 0;

  for (const { type, weight } of animationConfigs) {
    const anim = animations[type];
    if (anim) {
      const state = anim(pos, ctx, config);
      scale += state.scale * weight;
      opacity += state.opacity * weight;
      rotationOffset += state.rotationOffset * weight;
      totalWeight += weight;
    }
  }

  if (totalWeight > 0) {
    return {
      scale: scale / totalWeight,
      opacity: opacity / totalWeight,
      rotationOffset: rotationOffset / totalWeight,
    };
  }

  return { scale: 1, opacity: 1, rotationOffset: 0 };
}
